import { useEffect } from 'react';

/**
 * La coda dell'arena non sopravvive all'uscita dalla sezione.
 *
 * In arena l'overlay "avversario trovato" è disattivato e la conferma è
 * automatica: restando in coda mentre si gira l'app ci si ritrovava sulla
 * damiera partendo da Profilo, Puzzle o Regole, senza preavviso.
 *
 * La guardia esisteva già, ma stava dentro `handleNavigate` — cioè sulla sola
 * porta della navigazione (bottom nav e sidebar). Il gesto indietro passa da
 * `applyLocation`, e il tocco su un nome in classifica apre il profilo da
 * un'altra parte ancora: per quelle strade si usciva dalla sezione restando in
 * coda, ed era proprio il caso che la guardia voleva impedire.
 *
 * Qui la regola è una sola e guarda il risultato invece della strada: se sei
 * in coda e non sei nella sezione arena, ti metto in pausa. Non importa come
 * ci sei arrivato.
 *
 * Guardare la partita di un altro NON è uscire: è stare nell'arena. `spectate`
 * è quindi fra gli schermi che tengono in coda, e chi guarda resta
 * accoppiabile — quando la partita arriva, il client ci porta sopra.
 *
 * ── L'altra metà è altrove ────────────────────────────────────────────────
 *
 * «Non sei davvero lì» ha due facce, e questo hook ne copre UNA sola: il cambio
 * di schermata. La seconda — l'app mandata in secondo piano — stava qui e ci
 * stava male: era un `setTimeout` di cinque secondi che iOS non eseguiva mai
 * (sospende la pagina prima) e che su Android non veniva nemmeno armato se
 * l'app spariva mentre si stava giocando. Adesso è `useArenaPresence`, che si
 * limita a DICHIARARE l'assenza al server e lascia a lui l'attesa, su un
 * orologio che nessun sistema operativo può sospendere.
 *
 * Le due regole restano separate di proposito: questa è una regola di
 * navigazione e vale solo per chi è in coda; quella è una regola di visibilità
 * e vale per chiunque sia iscritto, partita in corso compresa.
 *
 * ⚠️ E non si ferma nessun orologio: chi è già in PARTITA non passa di qui
 * (`arenaStatus` non è più `searching`), e il suo tempo continua a scorrere.
 * È voluto: l'avversario sta aspettando.
 */

/**
 * Quanto si aspetta prima di uscire dalla coda per assenza.
 *
 * ⚠️ Non è più l'arena a usarla — la sua attesa vive adesso sul server, in
 * `ATTESA_ASSENZA_MS` — ma il proprio tavolo sì (`QueuedTableBanner`), con lo
 * stesso ragionamento e lo stesso difetto su iOS ancora aperto. Resta qui,
 * esportata, finché quella non riceve lo stesso trattamento.
 */
export const ATTESA_PRIMA_DI_USCIRE_DALLA_CODA_MS = 5_000;

/**
 * Gli schermi che sono ancora "dentro" la sezione arena.
 *
 * `spectate` c'è perché guardare una partita dell'arena è restare nell'arena:
 * chi guarda continua a essere accoppiabile e viene portato sulla propria
 * partita quando arriva. Ci si arriva anche dalla lista online e dalla
 * dashboard, e va bene lo stesso — la regola è sul risultato («sono ancora
 * dentro l'arena?») e non sulla porta da cui si è passati.
 *
 * `tournaments` ha preso il posto di `arena`, e per la stessa ragione: quella
 * schermata non esiste più, e l'elenco dei tornei È l'elenco delle arene — le
 * arene sono sue righe, sotto il filtro «Formato». Senza questo scambio, chi è
 * IN CODA e preme «indietro» dal dettaglio atterrerebbe lì e verrebbe messo in
 * pausa in silenzio: la stessa pagina da cui era entrato, letta come «è uscito
 * dall'arena».
 */
export const ARENA_SCREENS = ['tournaments', 'arena-detail', 'arena-results', 'arena-game', 'spectate'] as const;

export function isArenaScreen(screen: string): boolean {
  return (ARENA_SCREENS as readonly string[]).includes(screen);
}

export function useArenaQueueGuard(
  arenaStatus: string,
  screen: string,
  pauseSearch: (silenziosa?: boolean) => void,
): void {
  /* `true`: e' una pausa automatica, e un guasto di rete non va raccontato a
     schermo per un gesto che l'utente non ha fatto. */
  useEffect(() => {
    if (arenaStatus === 'searching' && !isArenaScreen(screen)) {
      pauseSearch(true);
    }
  }, [arenaStatus, screen, pauseSearch]);
}
